import { backend } from '@/services/backend'
import { QUESTIONNAIRE_GENRES } from './onboarding.types'
import type {
  QuestionnaireSection,
  QuestionnairePage,
  QuestionnaireUserChoices,
  QuestionnaireChoice,
  QuestionnaireGenre,
  QuestionnaireBook,
} from './onboarding.types'
import { Sentry } from '@/services/sentry'
import { storage } from '@/helpers/storage'
import { Mixpanel } from '@/analytics/mixpanel'
import { QUESTIONNAIRE_BOOKS } from './onboarding.books'
import {
  ONBOARDING_DEFAULT_SUBJECT,
  shouldSkipLearningSetupSummary,
} from './onboarding.personalization'
import type { OnboardingSubject } from './onboarding.personalization'
import { StorageKeys } from '@/helpers/storage.keys'
import { hoursNumberFromString } from '@/helpers/time'
import { resetCustomListsCache, resetDocsUserDataCache } from '@/helpers/cache'
import { getKnownContentOriginSlug } from '@/helpers/billingSourceBook'

export class QuestionnaireView {
  sections: QuestionnaireSection[]

  private _currentSectionIndex = 0
  private _currentPageIndex = 0
  private _currentSubpageIndex = 0

  private _userChoices: QuestionnaireUserChoices = {}
  private _currentChoice?: QuestionnaireChoice

  private _nextOnboardingPage: () => void

  private _isLoadingNextPage = false

  isContinueButtonVisible = true
  isLogoVisible = false

  /**
   * @param sections The pages data.
   * @param nextOnboardingPage Callback to advance to the next onboarding page
   * @param userName The user's given name. This value will be used to replace all instances of '{{name}}'
   *   in the pages' `variableTitle` property.
   */
  constructor(
    sections: QuestionnaireSection[],
    nextOnboardingPage: () => void,
    userName?: string,
  ) {
    if (userName) {
      this.sections = this._replaceVariables(sections, userName)
    } else {
      this.sections = sections
    }

    this._userChoices = QuestionnaireView.loadUserChoices()

    this.loadPosition()

    if (this.currentSectionIndex === 0 && this.currentPageIndex === 0) {
      this.trackPageView()
      // Save the position for the first page.
      this.savePosition()
    }

    if (this.currentPage.hideContinueButton) {
      this.isContinueButtonVisible = false
    }

    if (this.currentPage.showLogo) {
      this.isLogoVisible = true
    }

    this._nextOnboardingPage = nextOnboardingPage
  }

  /**
   * Replace the `title` property of the given pages with the value of their `variableTitle`,
   * and replace all instaces of '{{name}}' with the given `userName`.
   *
   * @param sections The pages data.
   * @param userName The user's given name. This value will be used to replace all instances of '{{name}}'
   *   in the pages' `variableTitle` property.
   */
  private _replaceVariables(
    sections: QuestionnaireSection[],
    userName: string,
  ): QuestionnaireSection[] {
    return sections.map((section) => {
      const parsedPages = section.pages.map((page) => {
        if (!page.variableTitle) {
          return page
        }
        return {
          ...page,
          title: page.variableTitle.replace(/{{name}}/g, userName),
        }
      })

      return {
        ...section,
        pages: parsedPages,
      }
    })
  }

  get pages(): QuestionnairePage[] {
    return this.sections.flatMap((section) => section.pages)
  }

  get currentChoice(): QuestionnaireChoice | undefined {
    return this._currentChoice
  }

  get userChoices(): QuestionnaireUserChoices {
    return this._userChoices
  }

  get currentSectionIndex(): number {
    return this._currentSectionIndex
  }

  get currentPageIndex(): number {
    return this._currentPageIndex
  }

  get currentSubpageIndex(): number {
    return this._currentSubpageIndex
  }

  /**
   * Get the current page object.
   */
  get currentPage(): QuestionnairePage {
    return this.sections[this.currentSectionIndex].pages[this.currentPageIndex]
  }

  /**
   * Get the next page object (if it exists).
   */
  get nextPage(): QuestionnairePage | undefined {
    // Return the next page in the current section, if it exists.
    const nextPageInCurrentSection =
      this.currentSection.pages[this._currentPageIndex + 1]
    if (nextPageInCurrentSection) {
      return nextPageInCurrentSection
    }

    // Otherwise, get the first page of the next section.
    if (this.nextSection) {
      return this.nextSection.pages[0]
    }
  }

  /**
   * Get the number of subpages in the current page.
   *
   * Returns `1` if it doesn't have subpages.
   */
  get currentPageLength(): number {
    return this.getPageLength(this.currentPage)
  }

  /**
   * Return `true` if the current subpage is the last subpage in the current page.
   */
  get isLastSubpage(): boolean {
    return this.currentSubpageIndex === this.currentPageLength - 1
  }

  /**
   * Get the current section object.
   */
  get currentSection(): QuestionnaireSection {
    return this.sections[this.currentSectionIndex]
  }

  /**
   * Get the next section object (if it exists).
   */
  get nextSection(): QuestionnaireSection | undefined {
    return this.sections[this.currentSectionIndex + 1]
  }

  /**
   * Get the number of pages in the current section, including subpages.
   */
  get currentSectionLength(): number {
    let count = 0

    const pages = this.currentSection.pages
    pages.map((page) => {
      count += this.getPageLength(page)
    })

    return count
  }

  /**
   * Get the length of the `pages` array of the current section.
   */
  get currentPagesLength(): number {
    return this.currentSection.pages.length
  }

  /**
   * Return `true` if the current section is the last section in the questionnaire.
   */
  get isLastSection(): boolean {
    return this.currentSectionIndex === this.sections.length - 1
  }

  /**
   * Return `true` if the current page is the last page in the current section.
   */
  get isSectionLastPage(): boolean {
    return this.currentPageIndex === this.currentPagesLength - 1
  }

  /**
   * Return `true` if the current page is the last page of the questionnaire.
   */
  get isLastPage(): boolean {
    return this.isLastSection && this.isSectionLastPage
  }

  /**
   * Return `true` while transitioning between questionnaire pages.
   */
  get isLoadingNextPage(): boolean {
    return this._isLoadingNextPage
  }

  /**
   * Get the progress towards the end of the current section.
   *
   * @Returns a decimal percentage value [0-1] representing the progress through the section.
   *
   * @example the 1st page in a 10 page section returns `0`
   * @example the 5th page in a 10 page section returns `0.4`
   * @example the 10th page in a 10 page section returns `1`
   */
  get currentSectionProgress(): number {
    return this.sectionPreviousPagesLength / this.currentSectionLength
  }

  /**
   * Get the number of pages visited in this section, including subpages.
   */
  get sectionPreviousPagesLength(): number {
    let count = this.currentSubpageIndex

    for (let i = 0; i < this.currentPageIndex; i++) {
      const page = this.currentSection.pages[i]
      count += this.getPageLength(page)
    }

    return count
  }

  /**
   * Get the number of subpages in the given page.
   *
   * Returns `1` if it doesn't have subpages.
   */
  getPageLength(page: QuestionnairePage): number {
    return page.content?.length || 1
  }

  /**
   * Get the page data with the given `pageID`.
   */
  getPage(pageID: string): QuestionnairePage | undefined {
    return this.pages.find((page) => page.id === pageID)
  }

  /**
   * Save the current Questionnaire position to local storage.
   */
  savePosition(): void {
    storage.setItem(
      StorageKeys.questionnairePosition,
      JSON.stringify({
        section: this.currentSectionIndex,
        page: this.currentPageIndex,
      }),
    )
  }

  /**
   * Update the Questionnaire position from the saved data in local sotrage.
   */
  loadPosition(): void {
    const positionString = storage.getItem(StorageKeys.questionnairePosition)
    if (!positionString) {
      return
    }

    const position = JSON.parse(positionString)

    this._currentSectionIndex = position.section
    this._currentPageIndex = position.page
  }

  /**
   * Update `this._userChoices` from the data saved in local storage.
   */
  static loadUserChoices(): QuestionnaireUserChoices {
    const choicesString = storage.getItem(StorageKeys.questionnaireChoices)
    if (!choicesString) {
      return {}
    }

    return JSON.parse(choicesString)
  }

  /**
   * Track the current page view and choices to Mixpanel.
   */
  trackPageView(): void {
    Mixpanel.trackOnboardingPageView(
      `questionnaire - ${this.currentPage.id}`,
      this.userChoices,
    )
  }

  /**
   * Navigate to the next page.
   *
   * Returns `false` if the current is the last
   * subpage, and `true` otherwise.
   *
   * It just returns a boolean instead of navigating
   * to the next page because on some pages we might want
   * to handle some extra logic before that.
   */
  nextSubpage(): boolean {
    if (this.isLastSubpage) {
      return false
    }

    this._currentSubpageIndex++
    return true
  }

  /**
   * If the user leaves subject screen without a choice,
   * skip the following "working towards" screen.
   */
  private shouldSkipWorkingTowardsPage(): boolean {
    if (
      this.currentPage.id !== 'what-subject-interests-you' ||
      this._currentChoice !== undefined
    ) {
      return false
    }
    return (
      this.currentSection.pages[this._currentPageIndex + 1]?.id ===
      'what-are-you-working-towards'
    )
  }

  private shouldCompleteQuestionnaire(): boolean {
    if (this.isLastPage) {
      return true
    }
    return (
      this.nextPage?.id === 'learning-setup-summary' &&
      shouldSkipLearningSetupSummary(this.userChoices)
    )
  }

  /**
   * Navigate to the next page.
   */
  async goNextPage(button: 'primary' | 'secondary' = 'primary'): Promise<void> {
    this._isLoadingNextPage = true

    if (this.currentPage.onSubmit) {
      try {
        await this.currentPage.onSubmit(this._currentChoice)
      } catch (error) {
        Sentry.captureException(error)
      }
    }

    const skipWorkingTowardsPage = this.shouldSkipWorkingTowardsPage()

    this.saveUserChoice()

    if (this.shouldCompleteQuestionnaire()) {
      await QuestionnaireView.submitUserChoices(this.userChoices)
      this._nextOnboardingPage()
      this._isLoadingNextPage = false
      return
    }

    if (this.nextPage?.onBeforeEnter) {
      try {
        await this.nextPage.onBeforeEnter()
      } catch (error) {
        Sentry.captureException(error)
      }
    }

    // Reset the current subpage index.
    this._currentSubpageIndex = 0

    this._advanceOnePosition()

    if (skipWorkingTowardsPage) {
      this._currentPageIndex++
    }

    // For the exp_samplefirst_onboarding_v10_20710,
    //  we need to skip some pages, based on the flow
    this.skipPageOnContinue(button)

    this.trackPageView()
    this.savePosition()

    if (this.currentPage.hideContinueButton) {
      this.hideContinueButton()
    } else {
      this.showContinueButton()
    }

    if (this.currentPage.showLogo) {
      this.showLogo()
    } else {
      this.hideLogo()
    }

    this._isLoadingNextPage = false
  }

  /**
   * Move `_currentSectionIndex`/`_currentPageIndex` forward by one page.
   *
   * Goes into the next section if the current page is the last one
   * in its section.
   */
  private _advanceOnePosition(): void {
    if (this.isSectionLastPage) {
      // Go to the first page of the next section.
      this._currentSectionIndex++
      this._currentPageIndex = 0
    } else {
      this._currentPageIndex++
    }
  }

  /**
   * Skip pages that shouldn't be shown on the current flow.
   */
  skipPageOnContinue(button: 'primary' | 'secondary' = 'primary'): void {
    while (!this.isLastPage && this._shouldSkipSampleFirstPage(button)) {
      this._advanceOnePosition()
    }
  }

  private _shouldSkipSampleFirstPage(button: 'primary' | 'secondary'): boolean {
    const id = this.currentPage.id
    if (
      id === 'book-selection-20710-after-validation' ||
      id === 'book-selection-20710-after-preview'
    ) {
      return button === 'primary'
    }

    const answerForPage: Record<string, string> = {
      'book-selection-20710': 'A specific book, article, or podcast',
      'options-picker-20710-topic': 'A subject I want to get into',
      'options-picker-20710-skill': 'Improving a certain skill',
      'habit-options-20710': 'Reading more regularly',
    }
    const answer = answerForPage[id]
    if (!answer) {
      return false
    }

    const cameFromBook = Boolean(getKnownContentOriginSlug())
    const choice = this.userChoices['what-brings-you-here']
    return cameFromBook || choice !== answer
  }

  /**
   * Skip both PlanSelection20676 and PaymentCapture20676
   * (exp_onboarding_plan_cc_capture_v2_20676, #20676) in one go. It's used when
   * the user clicks "Skip for now" on the Plan Selection page, since
   * `goNextPage()` only advances by a single page.
   */
  skipPlanCardCapture20676(): void {
    this._currentSubpageIndex = 0
    this._advanceOnePosition()
    this._advanceOnePosition()

    this.trackPageView()
    this.savePosition()

    if (this.currentPage.hideContinueButton) {
      this.hideContinueButton()
    } else {
      this.showContinueButton()
    }

    if (this.currentPage.showLogo) {
      this.showLogo()
    } else {
      this.hideLogo()
    }
  }

  /**
   * Go to the next subpage, if possible, or
   * navigate to the next page.
   */
  async continue(): Promise<void> {
    const nextSubpageExists = this.nextSubpage()

    if (!nextSubpageExists) {
      await this.goNextPage()
    }
  }

  /**
   * Set the value of `this._currentChoice`.
   */
  setCurrentChoice(newValue: QuestionnaireChoice | undefined): void {
    this._currentChoice = newValue
  }

  /**
   * Add the current page's choice to `this._userChoices`, and then
   * reset `this.currentPageChoice`.
   */
  saveUserChoice(): void {
    if (this._currentChoice === undefined) {
      return
    }

    // Don't add empty arrays to the choices object.
    if (
      Array.isArray(this._currentChoice) &&
      this._currentChoice.length === 0
    ) {
      this.setCurrentChoice(undefined)
      return
    }

    this._userChoices[this.currentPage.id] = this._currentChoice

    // The picked guide replaces any entry book stored at the start of onboarding.
    if (this.currentPage.type === 'BookSelection20710') {
      this._userChoices['sample-guide'] = this._currentChoice
    }

    // Make a copy of the user choices to local storage so we can
    // restore it later if they leave the questionnaire page and
    // come back later.
    storage.setItem(
      StorageKeys.questionnaireChoices,
      JSON.stringify(this.userChoices),
    )

    // Reset `this._currentChoice`.
    this.setCurrentChoice(undefined)
  }

  /*
  Get the book cover URL from the given URL slug.
  @param urlSlug - The URL slug of the book to get the cover for.
  @returns The cover image URL.
  */
  getBookCover(urlSlug: string): string {
    return `//media.shortform.com/covers/png/${urlSlug}-cover.png`
  }

  /**
   * Get the user choice from the given `pageID`.
   */
  getUserChoice<T extends QuestionnaireChoice>(pageID: string): T | undefined {
    return this.userChoices[pageID] as T | undefined
  }

  /*
  Get the subject interests you from the user choices.
  @returns The subject interests you.
  */
  get subjectInterestsYou(): OnboardingSubject {
    return (this.getUserChoice<string>('what-subject-interests-you') ??
      ONBOARDING_DEFAULT_SUBJECT) as OnboardingSubject
  }

  /**
   * Send `this._userChoices` to the backend.
   */
  static async submitUserChoices(
    userChoices: QuestionnaireUserChoices,
  ): Promise<void> {
    try {
      await backend.saveQuestionnaire(userChoices)
    } catch (error: any) {
      Sentry.withScope((scope) => {
        scope.setExtra('message', 'Onboarding Questionnaire aborted on error')
        Sentry.captureException(error)
      })
    }

    // Keep questionnaireChoices in local storage so billing can resolve the
    // positively scored onboarding book. Clear position so the questionnaire
    // does not resume mid-flow.
    storage.removeItem(StorageKeys.questionnairePosition)
  }

  /**
   * Hide the continue button.
   *
   * The continue button is the standard way to navigate through the questionnaire,
   * but some pages have their own logic to handle the navigation; so we can hide it
   * in those cases.
   */
  hideContinueButton(): void {
    this.isContinueButtonVisible = false
  }

  /**
   * Show the continue button.
   */
  showContinueButton(): void {
    this.isContinueButtonVisible = true
  }

  /**
   * Hide the logo at the top of the page.
   */
  hideLogo(): void {
    this.isLogoVisible = false
  }

  /**
   * Show the logo at the top of the page.
   */
  showLogo(): void {
    this.isLogoVisible = true
  }
}

/**
 * Import the given image from the onboarding images folder.
 */
export function getImage(image: string): string {
  return require(`@/assets.app/images/onboarding/${image}`)
}

/**
 * Get 15 books based on the given `selectedGenres`.
 *
 * This function returns an equal number of the most popular books
 * in each genre. If 15 is not divisible by the number of
 * selected genres, it divides the remainder among the first genres.
 * Eg. if `selectedGenres` has 2 items, this function will return
 * 8 books for the first genre, and 7 for the second.
 * If it has 6 items, this function will return 3 for the first 3,
 * and 2 for the last 3.
 */
export function getQuestionnaireBooks(
  selectedGenres: readonly QuestionnaireGenre[] = QUESTIONNAIRE_GENRES,
): QuestionnaireBook[] {
  const resultLength = 15
  const booksPerGenre = Math.floor(resultLength / selectedGenres.length)
  const remainder = resultLength % selectedGenres.length

  const booksToShow: QuestionnaireBook[] = []

  selectedGenres.forEach((genre, index) => {
    // Get books in the given genre that don't already exist
    // in `booksToShow` (ie. don't add duplicates).
    const booksInThisGenre = QUESTIONNAIRE_BOOKS.filter((book) => {
      return (
        book.genre.includes(genre) &&
        !booksToShow.some((existingBook) => existingBook.id === book.id)
      )
    })

    // Divide the remainer among the first genres.
    // prettier-ignore
    const numberOfBooksToAdd = index < remainder
      ? booksPerGenre + 1
      : booksPerGenre

    booksToShow.push(...booksInThisGenre.slice(0, numberOfBooksToAdd))
  })

  // Sort the books by popularity and return them.
  return booksToShow.sort((a, b) => a.popularity - b.popularity)
}

/**
 * Submit the user's chosen preferred notification time from
 * the `what-time-of-the-day` questionnaire page. Also infer their
 * timezone.
 */
export async function submitTimeOfDay(choice?: string): Promise<void> {
  const preferredTimeString = choice
    ? // Get the time string from the user's choice.
      choice.split(': ')[1]
    : // Default option in case the user didn't choose anything.
      '3:00 PM'
  const preferredTime = hoursNumberFromString(preferredTimeString)

  // We have to multiply it by -1 to un-invert the offset because,
  // for some reason, `getTimezoneOffset` returns the offset inverted
  // (eg. UTC-1 is 60, and UTC+1 is -60). Truly a WTFJS moment.
  const timezoneOffset = new Date().getTimezoneOffset() * -1

  await backend.putNotificationSettings({
    preferred_notification_time: preferredTime,
    timezone_offset: timezoneOffset,
  })
}

/**
 * Submit the user's inferred timezone.
 */
export async function updateUserTimezoneOffset(): Promise<void> {
  // We have to multiply it by -1 to un-invert the offset; see above.
  const timezoneOffset = new Date().getTimezoneOffset() * -1

  await backend.putNotificationSettings({
    timezone_offset: timezoneOffset,
  })
}

/**
 * Finish any pending first list initialization: the backend adds the
 * arrival and onboarding books to Want to Read.
 */
export async function initializeFirstLaunchList(): Promise<void> {
  try {
    const initialized = await backend.initializeFirstLaunchReadingList()
    if (initialized) {
      // Initialization can add several books to a list. Remove both the cached
      // lists and the cached per-book list memberships so later pages load the
      // initialized data from the backend.
      await Promise.all([resetCustomListsCache(), resetDocsUserDataCache()])
    }
    // Remove First Reading List (deprecated) book covers from local storage
    storage.removeItem(StorageKeys.questionnaireBooks)
  } catch (error) {
    Sentry.captureException(error)
  }
}
